import { EditPencil as EditPencilIcon, PageEdit as PageEditIcon, Trash as TrashIcon } from 'iconoir-react'
import { ReactElement, useState } from 'react'
import Button from '../Button'
import { FormMarkDownEditor } from '../form/FormMarkDownEditor'
import { SmartForm } from '../form/SmartForm'
import NameIcon from '../shared/NameIcon'
import { Tooltip } from '../Tooltip'
import { ActivityCard } from './ActivityCard'
import { ActivityTags } from './ActivityTags'
import { noteValidator } from 'app/components/contacts/drawer/CreateNoteModal'
import { MailInIcon } from 'app/components/Icon/MailInIcon'
import type { ActivityJson } from 'app/types/entities'
import { useWorkspace } from 'app/utils'
import { lastNameInitial } from 'lib/nameUtils'

type Props = {
  activity: ActivityJson
  isFirst?: boolean
  isLast?: boolean
  handleEdit?: (activityId: string, values: any) => void
  handleDelete?: (activityId: string) => void
}
/**
 * Renders Form activity record.
 * @Prop activity - Activity to query the Form from.
 * @Prop isFirst. Boolean. If true it hides the activity union line.
 * @Prop isLast. Boolean. If true it hides the activity union line.
 * @Prop handleEdit. Function. Callback to handle edit.
 * @Prop handleDelete. Function. Callback to handle delete.
 * @returns ReactElement
 */

export function NoteCard({ activity, isFirst, isLast, handleEdit, handleDelete }: Props): ReactElement {
  const workspace = useWorkspace()
  const [nameTooltip, setNameTooltip] = useState<string | undefined>(undefined)
  const [editable, setEditable] = useState<boolean>(false)
  const [alwaysVisible, setAlwaysVisible] = useState<boolean>(
    !activity?.description || (activity?.description?.split(`\n`).length < 2 && activity?.description?.length <= 80),
  )
  const title = (
    <div className="flex min-w-0 items-center gap-x-2 truncate">
      <span className="shrink-0 text-ui-sm text-strong">
        {activity.type}
        {activity.type === `Note` && <span className="text-body-sm text-subtle"> from</span>}
      </span>
      {activity.type === `Note` && (
        <NameIcon
          widthClassName="w-5"
          heightClassName="h-5"
          backgroundClassName="bg-surface-strong border border-default rounded-full"
          textClassName="text-strong"
          fullName={`${activity?.source?.[0]}${lastNameInitial(activity?.source ?? ` `)?.toLocaleUpperCase()}`}
          sizeStyle={{ fontSize: `8px` }}
        />
      )}
      <Tooltip
        className="min-w-0 truncate"
        tooltipClassName="max-w-xs break-words rounded-md bg-inverse px-2 py-1 text-ui-xs text-inverse"
        message={nameTooltip}
        onMouseOver={(event) => {
          const el = event.currentTarget as HTMLElement
          const isOverflowing = el.scrollWidth > el.clientWidth
          setNameTooltip(isOverflowing ? activity.source || undefined : undefined)
        }}
      >
        <span className="text-ui-sm text-strong">{activity.source}</span>
      </Tooltip>
    </div>
  )

  return (
    <ActivityCard
      icon={
        activity.type === `Note` ? (
          <PageEditIcon className="h-5 w-5 rounded-md bg-indigo-200 p-1 text-strong" />
        ) : (
          <MailInIcon className="h-5 w-5 rounded-md bg-indigo-200 p-1 text-strong" />
        )
      }
      title={title}
      actions={
        activity.type === `Note` && (
          <div className="flex gap-x-1 p-0.5">
            <Tooltip message={`Edit ${activity.type}`}>
              <EditPencilIcon
                className="h-3 w-3 cursor-pointer"
                strokeWidth={1}
                aria-label={`Edit ${activity.type}`}
                onClick={() => setEditable((prev) => (prev === false ? true : prev))}
              />
            </Tooltip>
            <Tooltip message={`Delete ${activity.type}`}>
              <TrashIcon
                className="h-3 w-3 cursor-pointer"
                strokeWidth={1}
                aria-label={`Delete ${activity.type}`}
                onClick={() => handleDelete?.(activity?.id!)}
              />
            </Tooltip>
          </div>
        )
      }
      activityDate={activity?.activityDate}
      tags={<ActivityTags activityTags={activity?.tags} workspaceId={workspace?.id} />}
      preview={
        <input
          type="text"
          disabled
          value={activity.description}
          className="mt-2 flex w-full items-center overflow-y-hidden truncate rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm"
        />
      }
      drawer={
        <div className="mt-2 w-full items-center rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm">
          <SmartForm
            id="note-edit-form"
            validator={noteValidator}
            hiddenInputs={[
              { name: `type`, value: `Note` },
              { name: `contactId`, value: activity.contacts?.[0]?.id },
            ]}
            className="!mt-0 !gap-y-3 px-0"
            onSubmit={(data) => {
              handleEdit?.(activity.id!, data)
              setEditable(false)
              setAlwaysVisible(data.description.split(`\n`).length < 2 && data.description.length <= 80)
            }}
            defaultValues={{ ...activity }}
            preventSubmit
            hideButtons
          >
            <FormMarkDownEditor
              richTextEditorNamespace="Edit-Note"
              name="description"
              placeholder="Type your note here..."
              errorClassName="text-red-500"
              editable={editable}
              description={activity.description}
              cols={6}
            />
            {editable && (
              <div className="col-span-6 flex justify-end gap-2 bg-surface">
                <Button
                  label="Cancel"
                  mode="white"
                  className="!text-trn-black h-6 justify-center !px-3 !text-ui-xs"
                  size="sm"
                  onClick={() => setEditable(false)}
                />

                <Button
                  label="Save"
                  mode="primary"
                  type="submit"
                  size="sm"
                  className="!text-trn-black h-6 justify-self-start !px-3 !text-ui-xs"
                />
              </div>
            )}
          </SmartForm>
        </div>
      }
      isFirst={isFirst}
      isLast={isLast}
      alwaysOpen={alwaysVisible}
    />
  )
}
